import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Box, Divider, Typography, useMediaQuery } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useDispatch } from "react-redux";
import { Link as RouterLink, useHistory, useLocation } from "react-router-dom";
import GiftCheckoutStepper from "components/v3/pages/account/GiftCheckoutStepper";
import SideMenuLayout from "components/v3/pages/onboarding/SideMenuLayout";
import LoadingAnimation from "components/v3/sharedComponents/LoadingAnimation";
import GLButton from "components/v3/sharedComponents/GLButton";
import { COLORS } from "components/v3/Theme/colors";
import {
  checkoutUsesTopLevelNavigation,
  giftPaymentUsesStaticPreview
} from "constants/checkoutNavigationConstants";
import { GIFT_UNIT_PRICE_USD } from "constants/membershipPriceConstants";
import { PATHS } from "constants/pathConstants";
import { openGLSnackbar } from "store/reducers/snackbar/openGLSnackbarSlice";
import PageTitle from "utils/PageTitle";

const LOADING_VIEWPORT_MAX_PX = 500;
const IFRAME_MIN_HEIGHT_PX = 850;
const IFRAME_MIN_HEIGHT_NARROW_EXTRA_PX = 200;
const IFRAME_NARROW_MAX_WIDTH_CSS = "(max-width: 526px)";
const EXPAND_MAX_PX = 1600;
const IFRAME_ROUNDED_CORNER_RADIUS_PX = 10;
const IFRAME_ROUNDED_CORNER_PADDING_PX = 10;
const IFRAME_CHROME_VERTICAL_EXTRA_PX = IFRAME_ROUNDED_CORNER_PADDING_PX * 2;

const STATIC_PREVIEW_DELAY_MS = 3000;
const STATIC_PREVIEW_IMAGE_MAX_WIDTH_PX = 450;
const giftPaymentPreviewImageSrc = `${process.env.PUBLIC_URL}/gift-payment-form-preview.png`;

/** Set by `GiftRecipientsPage` after a successful referrals payment-link POST. */
type GiftPaymentLocationState = {
  ecommerceLink: string;
  giftQuantity?: number;
};

const GiftPaymentPage: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const routeState = location.state as GiftPaymentLocationState | undefined;
  const ecommerceLink = routeState?.ecommerceLink ?? null;

  const isNarrowPaymentViewport = useMediaQuery(IFRAME_NARROW_MAX_WIDTH_CSS, {
    defaultMatches: false
  });
  const iframeMinHeightLoaded =
    IFRAME_MIN_HEIGHT_PX +
    (isNarrowPaymentViewport ? IFRAME_MIN_HEIGHT_NARROW_EXTRA_PX : 0);
  const outerMinHeightLoading =
    LOADING_VIEWPORT_MAX_PX + IFRAME_CHROME_VERTICAL_EXTRA_PX;
  const outerMinHeightLoaded =
    iframeMinHeightLoaded + IFRAME_CHROME_VERTICAL_EXTRA_PX;
  const outerMaxHeightLoaded = EXPAND_MAX_PX + IFRAME_CHROME_VERTICAL_EXTRA_PX;

  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [staticPreviewReady, setStaticPreviewReady] = useState(false);

  const planCount = Math.max(1, routeState?.giftQuantity ?? 1);
  const formattedUnitPrice = useMemo(
    () =>
      new Intl.NumberFormat("en-US", {
        currency: "USD",
        style: "currency"
      }).format(GIFT_UNIT_PRICE_USD),
    []
  );
  const formattedTotal = useMemo(
    () =>
      new Intl.NumberFormat("en-US", {
        currency: "USD",
        style: "currency"
      }).format(planCount * GIFT_UNIT_PRICE_USD),
    [planCount]
  );

  useEffect(() => {
    setIframeLoaded(false);
    setStaticPreviewReady(false);
  }, [ecommerceLink]);

  useEffect(() => {
    if (!giftPaymentUsesStaticPreview || !ecommerceLink) {
      return undefined;
    }
    const timerId = globalThis.setTimeout(() => {
      setStaticPreviewReady(true);
    }, STATIC_PREVIEW_DELAY_MS);
    return () => globalThis.clearTimeout(timerId);
  }, [ecommerceLink]);

  useEffect(() => {
    const state = location.state as GiftPaymentLocationState | undefined;
    if (!state?.ecommerceLink) {
      history.replace(PATHS.ACCOUNT_MANAGEMENT_GIFT_RECIPIENTS);
    }
  }, [history, location]);

  useEffect(() => {
    globalThis.scrollTo(0, 0);
  }, []);

  useLayoutEffect(() => {
    if (
      checkoutUsesTopLevelNavigation &&
      ecommerceLink &&
      !giftPaymentUsesStaticPreview
    ) {
      globalThis.location.replace(ecommerceLink);
    }
  }, [ecommerceLink]);

  PageTitle("Buy gift");

  const paymentPanelExpanded = giftPaymentUsesStaticPreview
    ? staticPreviewReady
    : iframeLoaded;

  const mainContent = ecommerceLink ? (
    checkoutUsesTopLevelNavigation ? (
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          justifyContent: "center",
          minHeight: 360,
          py: 4,
          width: "100%"
        }}
      >
        <LoadingAnimation />
      </Box>
    ) : (
    <Box
      sx={{
        boxSizing: "border-box",
        display: "flex",
        flexDirection: { lg: "row", xs: "column" },
        gap: 4,
        paddingX: { lg: 0, xs: 2 },
        paddingBottom: 4,
        width: "100%"
      }}
    >
      <Box
        sx={{
          bgcolor: COLORS.WHITE,
          borderRadius: "12px",
          boxSizing: "border-box",
          display: "flex",
          flex: 1,
          flexDirection: "column",
          gap: 0,
          minWidth: 0,
          p: { lg: 4, xs: 3 }
        }}
      >
        <Typography
          component="h1"
          sx={{ typography: { xs: "h5_black", md: "h4_black" } }}
        >
          Payment details
        </Typography>
        <Divider
          sx={{
            borderColor: alpha(COLORS.DARK_GRAY, 0.25),
            mb: 3,
            mt: 2
          }}
        />

        <Box
          sx={{
            position: "relative",
            boxSizing: "border-box",
            maxHeight: paymentPanelExpanded
              ? outerMaxHeightLoaded
              : LOADING_VIEWPORT_MAX_PX + IFRAME_CHROME_VERTICAL_EXTRA_PX,
            maxWidth: "100%",
            minHeight: paymentPanelExpanded
              ? outerMinHeightLoaded
              : outerMinHeightLoading,
            minWidth: 0,
            mx: "auto",
            overflow: paymentPanelExpanded ? "visible" : "hidden",
            width: "100%"
          }}
        >
          <Box
            sx={{
              backgroundColor: COLORS.WHITE,
              borderRadius: `${IFRAME_ROUNDED_CORNER_RADIUS_PX}px`,
              boxSizing: "border-box",
              maxWidth: "100%",
              minWidth: 0,
              overflow: "hidden",
              p: `${IFRAME_ROUNDED_CORNER_PADDING_PX}px`,
              width: "100%"
            }}
          >
            {giftPaymentUsesStaticPreview ? (
              <Box
                component="img"
                alt="Payment form preview"
                src={giftPaymentPreviewImageSrc}
                sx={{
                  display: staticPreviewReady ? "block" : "none",
                  height: "auto",
                  marginLeft: "auto",
                  marginRight: "auto",
                  maxWidth: `${STATIC_PREVIEW_IMAGE_MAX_WIDTH_PX}px`,
                  minWidth: 0,
                  width: "100%"
                }}
              />
            ) : (
              <iframe
                key={ecommerceLink}
                title="Gift payment"
                src={ecommerceLink}
                onLoad={() => setIframeLoaded(true)}
                style={{
                  border: "none",
                  display: "block",
                  marginLeft: "auto",
                  marginRight: "auto",
                  maxWidth: "100%",
                  minHeight: iframeLoaded
                    ? iframeMinHeightLoaded
                    : LOADING_VIEWPORT_MAX_PX,
                  minWidth: 0,
                  opacity: iframeLoaded ? 1 : 0,
                  transition:
                    "min-height 450ms cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease-in",
                  width: "100%"
                }}
              />
            )}
          </Box>
          {!paymentPanelExpanded && (
            <Box
              sx={{
                alignItems: "center",
                bgcolor: "transparent",
                display: "flex",
                inset: 0,
                justifyContent: "center",
                position: "absolute"
              }}
            >
              <LoadingAnimation />
            </Box>
          )}
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <GLButton
            buttonType="secondary"
            onClick={() => {
              dispatch(
                openGLSnackbar({
                  variant: "success",
                  header: "",
                  subText: "referral sent!"
                })
              );
              history.push(PATHS.ACCOUNT_MANAGEMENT);
            }}
            text="Pretend to pay (but not like fraud pretend)"
          />
        </Box>
      </Box>

      <Box
        sx={{
          alignSelf: { lg: "flex-start", xs: "stretch" },
          bgcolor: COLORS.BACKGROUND_BLUE,
          borderRadius: "12px",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          gap: 2,
          p: 2.5,
          width: { lg: 320, xs: "100%" }
        }}
      >
        <Typography component="h2" variant="body_strong_black">
          Gifting A Membership
        </Typography>
        <Typography sx={{ color: COLORS.DARK_GRAY_2 }} variant="body2">
          Genomic Life Complete Plan: Your additional cost
        </Typography>
        <Typography sx={{ fontWeight: 700 }} variant="body_strong_black">
          {planCount} x {formattedUnitPrice}
        </Typography>
        <Typography
          sx={{
            color: COLORS.ACCENT2,
            fontSize: "1.25rem",
            fontWeight: 700
          }}
          variant="body_strong_black"
        >
          {formattedTotal}
        </Typography>
        <Divider sx={{ borderColor: alpha(COLORS.DARK_GRAY, 0.25) }} />
        <Typography
          component="p"
          sx={{ color: COLORS.DARK_GRAY, fontStyle: "italic", m: 0 }}
          variant="body2"
        >
          → auto-renews yearly
          <br />→ cancel in 1 year
        </Typography>
        <Typography
          component="p"
          sx={{ color: COLORS.DARK_GRAY, m: 0 }}
          variant="body2"
        >
          Subscriptions automatically renew annually unless canceled in advance.
          Learn more in our{" "}
          <Box
            component={RouterLink}
            rel="noopener noreferrer"
            target="_blank"
            to={PATHS.ACCOUNT_MANAGEMENT_TERMS_AND_CONDITIONS}
            sx={{
              color: COLORS.PRIMARY,
              fontWeight: 600,
              textDecoration: "underline",
              textUnderlineOffset: "2px"
            }}
          >
            Terms and Conditions
          </Box>
          .
        </Typography>
        <Typography
          component="p"
          sx={{ color: COLORS.DARK_GRAY_2, lineHeight: 1.65, m: 0, mt: 1 }}
          variant="body2"
        >
          Your gift unlocks the full Genomic Life experience—insights from DNA
          screening, guidance on personal health risks, and one-on-one support
          when it matters most.
        </Typography>
      </Box>
    </Box>
    )
  ) : (
    <Box
      sx={{
        alignItems: "center",
        display: "flex",
        justifyContent: "center",
        minHeight: 360,
        py: 4,
        width: "100%"
      }}
    >
      <LoadingAnimation />
    </Box>
  );

  return (
    <SideMenuLayout
      onboardingStepper={<GiftCheckoutStepper activeStepIndex={1} />}
      stepperPlacement="top"
    >
      {mainContent}
    </SideMenuLayout>
  );
};

export default GiftPaymentPage;
